import {memo,useEffect,useMemo,useRef} from "react";
import {useGSAP} from "@gsap/react";
import gsap from "gsap";
import {MotionPathPlugin} from "gsap/MotionPathPlugin";
import type {LiveBlock,OriginBreakdown} from "../../shared/types";
import {formatCoverageBps,formatFlr} from "../../shared/format";
import flrLogo from "../assets/flr-logo.svg";
import type {BlockPresentationChannel} from "./blockPresentation";
import {blockSourceFamilies} from "./burnSankeyMath";

gsap.registerPlugin(useGSAP,MotionPathPlugin);

const MAX_DIRECT_SOURCES=6;
const SOURCE_NODE_X=290;
const SOURCE_ROUTE_X=300;
const MERGE_X=526;
const COLLECTOR_X=516;
const CORE_INPUT_X=570;
const CORE_OUTPUT_X=650;
const FLAME_X=1018;
const SOURCE_STREAM_WIDTH=6;

interface FlowSource {
  key:string;
  label:string;
  amountWei:string;
  shareBps:number;
  groupedCount?:number;
}

function route(sourceY:number,centerY:number):string{
  return `M ${SOURCE_ROUTE_X} ${sourceY} C 405 ${sourceY}, 480 ${centerY}, ${MERGE_X} ${centerY}`;
}

function formatShare(shareBps:number):string{
  return formatCoverageBps(String(shareBps));
}

function selectFlowSources(origins:OriginBreakdown[]):FlowSource[]{
  const sorted=[...origins]
    .filter((origin)=>BigInt(origin.amountWei)>0n)
    .sort((left,right)=>BigInt(right.amountWei)>BigInt(left.amountWei)?1:-1);
  if(sorted.length<=MAX_DIRECT_SOURCES)return sorted.map((source)=>({...source,key:source.family}));

  const selected=new Set<OriginBreakdown>(sorted.slice(0,4));
  for(const priority of ["fdc","fassets"]){
    const source=sorted.find((candidate)=>candidate.family===priority);
    if(source&&selected.size<MAX_DIRECT_SOURCES)selected.add(source);
  }
  for(const source of sorted){
    if(selected.size>=MAX_DIRECT_SOURCES)break;
    selected.add(source);
  }

  const direct=sorted.filter((source)=>selected.has(source));
  const grouped=sorted.filter((source)=>!selected.has(source));
  return [
    ...direct.map((source)=>({...source,key:source.family})),
    {
      key:"other-measured",
      label:"Other measured activity",
      amountWei:grouped.reduce((total,source)=>total+BigInt(source.amountWei),0n).toString(),
      shareBps:grouped.reduce((total,source)=>total+source.shareBps,0),
      groupedCount:grouped.length,
    },
  ];
}

function blockSignal(block:LiveBlock):string{
  const sourceCount=blockSourceFamilies(block).length;
  return `Block ${new Intl.NumberFormat("en-GB").format(Number(block.blockNumber))} · ${sourceCount} ${sourceCount===1?"source":"sources"} · ${formatFlr(block.totalBurnWei)} FLR`;
}

export const BurnSankey=memo(function BurnSankey({origins,totalBurnWei,presentation}:{origins:OriginBreakdown[];totalBurnWei:string;presentation:BlockPresentationChannel}){
  const root=useRef<HTMLElement>(null);
  const signalText=useRef<HTMLSpanElement>(null);
  const playBlock=useRef<(block:LiveBlock)=>void>(()=>undefined);
  const ambientAnimations=useRef<gsap.core.Tween[]>([]);
  const allSources=useMemo(()=>[...origins]
    .filter((origin)=>BigInt(origin.amountWei)>0n)
    .sort((left,right)=>BigInt(right.amountWei)>BigInt(left.amountWei)?1:-1),[origins]);
  const sources=useMemo(()=>selectFlowSources(origins),[origins]);
  const visibleRef=useRef(typeof IntersectionObserver==="undefined");
  const height=Math.max(470,sources.length*58+64);
  const centerY=height/2;
  const positions=sources.map((_source,index)=>sources.length===1?centerY:50+(index*(height-100))/(sources.length-1));
  const signature=sources.map((source)=>`${source.key}:${source.amountWei}`).join("|");
  const leadingSource=allSources[0];

  useEffect(()=>{
    if(!root.current)return;
    if(typeof IntersectionObserver==="undefined"){
      visibleRef.current=true;
      return;
    }
    const observer=new IntersectionObserver(([entry])=>{
      visibleRef.current=entry.isIntersecting;
      for(const animation of ambientAnimations.current){
        if(entry.isIntersecting)animation.resume();
        else animation.pause();
      }
    },{rootMargin:"140px 0px"});
    observer.observe(root.current);
    return()=>observer.disconnect();
  },[signature]);

  useEffect(()=>presentation.subscribe((block)=>{
    if(signalText.current)signalText.current.textContent=blockSignal(block);
    playBlock.current(block);
  }),[presentation]);

  useGSAP(()=>{
    const outerFlame=root.current?.querySelector<SVGPathElement>(".burn-sankey__flame-outer");
    const innerFlame=root.current?.querySelector<SVGPathElement>(".burn-sankey__flame-inner");
    const media=gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)",()=>{
      const paused=!visibleRef.current;
      ambientAnimations.current=[
        ...(outerFlame?[gsap.to(outerFlame,{scaleX:.94,scaleY:1.1,rotation:-2,duration:.72,ease:"sine.inOut",repeat:-1,yoyo:true,transformOrigin:"50% 100%",paused})]:[]),
        ...(innerFlame?[gsap.to(innerFlame,{y:-2,scaleY:1.08,duration:.54,ease:"sine.inOut",repeat:-1,yoyo:true,transformOrigin:"50% 100%",paused})]:[]),
      ];
      return()=>{ambientAnimations.current=[];};
    });
    return()=>media.revert();
  },{scope:root,dependencies:[signature],revertOnUpdate:true});

  useGSAP((_context,contextSafe)=>{
    const reduceMotion=window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const routeElements=new Map(Array.from(root.current?.querySelectorAll<SVGPathElement>(".burn-sankey__route[data-source-key]")??[]).map((path)=>[path.dataset.sourceKey??"",path]));
    const particles=new Map(Array.from(root.current?.querySelectorAll<SVGCircleElement>(".burn-sankey__particle[data-source-key]")??[]).map((particle)=>[particle.dataset.sourceKey??"",particle]));
    const sourceGroups=new Map(Array.from(root.current?.querySelectorAll<SVGGElement>(".burn-sankey__source[data-source-key]")??[]).map((group)=>[group.dataset.sourceKey??"",group]));
    const sourcePulses=new Map(Array.from(root.current?.querySelectorAll<SVGCircleElement>(".burn-sankey__source-pulse[data-source-key]")??[]).map((pulse)=>[pulse.dataset.sourceKey??"",pulse]));
    const collectorRoute=root.current?.querySelector<SVGPathElement>(".burn-sankey__collector-route");
    const collectorParticle=root.current?.querySelector<SVGCircleElement>(".burn-sankey__particle--collector");
    const outputRoute=root.current?.querySelector<SVGPathElement>(".burn-sankey__out-route");
    const outputParticle=root.current?.querySelector<SVGCircleElement>(".burn-sankey__particle--out");
    const coreRing=root.current?.querySelector<SVGCircleElement>(".burn-sankey__core-ring--outer");
    const flame=root.current?.querySelector<SVGGElement>(".burn-sankey__flame");
    const animatedElements=[...particles.values(),...(collectorParticle?[collectorParticle]:[]),...(outputParticle?[outputParticle]:[])];
    const groupedKey=sources.some((source)=>source.key==="other-measured")?"other-measured":null;
    let timeline:gsap.core.Timeline|null=null;

    const clearActive=()=>{
      for(const group of sourceGroups.values())group.classList.remove("burn-sankey__source--active");
    };
    const stopPulse=()=>{
      gsap.set(animatedElements,{autoAlpha:0});
      gsap.set([...sourcePulses.values()],{autoAlpha:0,attr:{r:7}});
      if(timeline){
        timeline.kill();
        timeline=null;
      }
      clearActive();
    };
    const finishRaw=()=>{timeline=null;clearActive();};
    const finish=contextSafe?.(finishRaw)??finishRaw;
    const presentRaw=(block:LiveBlock)=>{
      stopPulse();
      if(reduceMotion||!visibleRef.current||BigInt(block.totalBurnWei)===0n)return;
      const families=blockSourceFamilies(block);
      const sourceKeys=[...new Set(families.map((family):string|null=>routeElements.has(family)?family:groupedKey).filter((key):key is string=>key!==null))];
      if(!sourceKeys.length)return;
      for(const key of sourceKeys)sourceGroups.get(key)?.classList.add("burn-sankey__source--active");

      const next=gsap.timeline({onComplete:finish,defaults:{overwrite:"auto"}});
      next.addLabel("sources",0);
      for(const key of sourceKeys){
        const path=routeElements.get(key);const particle=particles.get(key);const pulse=sourcePulses.get(key);
        if(!path||!particle)continue;
        next.set(particle,{autoAlpha:0,scale:.68,motionPath:{path,align:path,alignOrigin:[.5,.5],start:0,end:0}},"sources");
        next.to(particle,{duration:.4,ease:"power1.inOut",motionPath:{path,align:path,alignOrigin:[.5,.5],autoRotate:false,start:0,end:1}},"sources+=0.02");
        next.to(particle,{autoAlpha:1,scale:1,duration:.06,ease:"power1.out"},"sources+=0.05");
        next.to(particle,{autoAlpha:0,duration:.04,ease:"none"},"sources+=0.39");
        if(pulse){
          next.set(pulse,{autoAlpha:.72,attr:{r:7}},"sources");
          next.to(pulse,{autoAlpha:0,attr:{r:10},duration:.24,ease:"power2.out"},"sources");
        }
      }
      next.addLabel("collector",.37);
      if(collectorRoute&&collectorParticle){
        next.set(collectorParticle,{autoAlpha:0,scale:.76,motionPath:{path:collectorRoute,align:collectorRoute,alignOrigin:[.5,.5],start:0,end:0}},"collector");
        next.to(collectorParticle,{autoAlpha:1,scale:1.08,duration:.04,ease:"power1.out"},"collector");
        next.to(collectorParticle,{duration:.18,ease:"power1.inOut",motionPath:{path:collectorRoute,align:collectorRoute,alignOrigin:[.5,.5],autoRotate:false,start:0,end:1}},"collector");
        next.set(collectorParticle,{autoAlpha:0},"collector+=0.18");
      }
      next.addLabel("outflow",.53);
      if(coreRing)next.fromTo(coreRing,{scale:1,autoAlpha:.3},{scale:1.14,autoAlpha:.9,duration:.16,ease:"power2.out",repeat:1,yoyo:true,svgOrigin:`610 ${centerY}`},"outflow-=0.06");
      if(outputRoute&&outputParticle){
        next.set(outputParticle,{autoAlpha:0,scale:.7,motionPath:{path:outputRoute,align:outputRoute,alignOrigin:[.5,.5],start:0,end:0}},"outflow");
        next.to(outputParticle,{duration:.46,ease:"power1.inOut",motionPath:{path:outputRoute,align:outputRoute,alignOrigin:[.5,.5],autoRotate:false,start:0,end:1}},"outflow");
        next.to(outputParticle,{autoAlpha:1,scale:1,duration:.04},"outflow+=0.03");
        next.set(outputParticle,{autoAlpha:0},"outflow+=0.47");
      }
      if(flame)next.fromTo(flame,{scale:1},{scale:1.13,duration:.09,ease:"power2.out",repeat:1,yoyo:true,transformOrigin:"50% 50%"},"outflow+=0.41");
      timeline=next;
    };
    const present=contextSafe?.(presentRaw)??presentRaw;
    playBlock.current=present;

    return()=>{
      playBlock.current=()=>undefined;
      stopPulse();
      gsap.set(animatedElements,{autoAlpha:0,x:0,y:0,scale:1});
    };
  },{scope:root,dependencies:[signature,centerY],revertOnUpdate:true});

  if(!sources.length)return(
    <section className="burn-sankey" aria-labelledby="burn-sankey-title">
      <div className="section-heading"><div><span className="eyebrow">Verified burn provenance</span><h2 id="burn-sankey-title">The Burn Funnel</h2></div></div>
      <p className="empty-state">Burn-origin data will appear after the first complete daily observation.</p>
    </section>
  );

  const collectorPath=`M ${COLLECTOR_X} ${centerY} C 536 ${centerY}, 552 ${centerY}, ${CORE_INPUT_X} ${centerY}`;
  const outputPath=`M ${CORE_OUTPUT_X} ${centerY} C 780 ${centerY}, 890 ${centerY}, ${FLAME_X} ${centerY}`;
  return(
    <section ref={root} className="burn-sankey" aria-labelledby="burn-sankey-title">
      <div className="section-heading burn-sankey__heading">
        <div><span className="eyebrow">30-day provenance · confirmed block pulses</span><h2 id="burn-sankey-title">The Burn Funnel</h2></div>
        <div className="burn-sankey__summary-stats">
          <span><small>Total removed</small><strong>{formatFlr(totalBurnWei)} FLR</strong></span>
          {leadingSource&&<span><small>Leading source</small><strong>{leadingSource.label}</strong><em>{formatShare(leadingSource.shareBps)}</em></span>}
        </div>
      </div>
      <p className="sr-only" id="burn-sankey-summary">{allSources.map((source)=>`${source.label}: ${formatFlr(source.amountWei)} FLR`).join(". ")}. Total permanently burned: {formatFlr(totalBurnWei)} FLR. Particles represent the burn origins in confirmed blocks.</p>
      <div className="burn-sankey__viewport" role="img" aria-describedby="burn-sankey-summary">
        <svg viewBox={`0 0 1200 ${height}`} aria-hidden="true">
          <defs>
            <radialGradient id="flr-core-halo"><stop offset="0" stopColor="#ff8dc7" stopOpacity=".38"/><stop offset=".45" stopColor="#ff4fa3" stopOpacity=".15"/><stop offset="1" stopColor="#ff4fa3" stopOpacity="0"/></radialGradient>
            <linearGradient id="burn-out-gradient" x1="0" x2="1"><stop offset="0" stopColor="#ff4fa3" stopOpacity=".76"/><stop offset=".75" stopColor="#ff8dc7" stopOpacity=".94"/><stop offset="1" stopColor="#ffe4f3"/></linearGradient>
            <linearGradient id="burn-flame-gradient" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor="#ff4fa3"/><stop offset=".58" stopColor="#ff78bd"/><stop offset="1" stopColor="#ffd8ee"/></linearGradient>
            <filter id="burn-particle-glow" x="-250%" y="-250%" width="500%" height="500%"><feGaussianBlur stdDeviation="2" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          </defs>
          <g className="burn-sankey__grid"><path d={`M 290 24 V ${height-24}`}/><path d={`M 610 24 V ${height-24}`}/><path d={`M 1018 24 V ${height-24}`}/></g>
          {sources.map((source,index)=>{
            const path=route(positions[index],centerY);
            return <g data-source-key={source.key} className={`burn-sankey__source${source.groupedCount?" burn-sankey__source--grouped":""}`} key={source.key}>
              <path className="burn-sankey__flow-bed" d={path} strokeWidth={SOURCE_STREAM_WIDTH+4}/>
              <path className="burn-sankey__route" data-source-key={source.key} d={path} strokeWidth={SOURCE_STREAM_WIDTH}><title>{source.label}: {formatFlr(source.amountWei)} FLR</title></path>
              <circle className="burn-sankey__source-pulse" data-source-key={source.key} cx={SOURCE_NODE_X} cy={positions[index]} r="7"/>
              <circle className="burn-sankey__source-node" cx={SOURCE_NODE_X} cy={positions[index]} r="5"/>
              <text className="burn-sankey__source-name" x="24" y={positions[index]-6}>{source.label}</text>
              <text className="burn-sankey__source-value" x="24" y={positions[index]+13}>{formatFlr(source.amountWei)} FLR · {formatShare(source.shareBps)}{source.groupedCount?` · ${source.groupedCount} sources`:""}</text>
              <circle data-source-key={source.key} className="burn-sankey__particle" cx={SOURCE_ROUTE_X} cy={positions[index]} r="3.2"/>
            </g>;
          })}
          <path className="burn-sankey__flow-bed burn-sankey__flow-bed--collector" d={collectorPath} strokeWidth="18"/>
          <path className="burn-sankey__collector-route" d={collectorPath} strokeWidth="12"/>
          <circle className="burn-sankey__particle burn-sankey__particle--collector" cx={COLLECTOR_X} cy={centerY} r="3.8"/>
          <path className="burn-sankey__flow-bed burn-sankey__flow-bed--out" d={outputPath} strokeWidth="18"/>
          <path className="burn-sankey__out-route" d={outputPath} strokeWidth="12"/>
          <circle className="burn-sankey__particle burn-sankey__particle--out" cx={CORE_OUTPUT_X} cy={centerY} r="4"/>
          <g className="burn-sankey__core">
            <circle className="burn-sankey__core-halo" cx="610" cy={centerY} r="104"/>
            <circle className="burn-sankey__core-ring burn-sankey__core-ring--outer" cx="610" cy={centerY} r="68"/>
            <circle className="burn-sankey__core-ring" cx="610" cy={centerY} r="56"/>
            <circle className="burn-sankey__core-disc" cx="610" cy={centerY} r="46"/>
            <image href={flrLogo} x="579" y={centerY-31} width="62" height="62"/>
          </g>
          <text className="burn-sankey__core-label" x="610" y={centerY+92}>FLR</text>
          <g className="burn-sankey__flame" transform={`translate(${FLAME_X} ${centerY})`}>
            <circle className="burn-sankey__flame-chamber" r="24"/>
            <path className="burn-sankey__flame-outer" d="M0 19C-11 16-17 7-12-4c2-5 7-9 8-17 8 7 10 14 8 20 4-3 7-8 6-14 8 8 12 16 9 24C16 18 9 23 0 19Z"/>
            <path className="burn-sankey__flame-inner" d="M1 15c-6-3-8-8-4-14 1 5 5 6 5 10 1-4 5-7 4-12 5 5 7 11 4 15-2 3-6 4-9 1Z"/>
          </g>
          <text className="burn-sankey__out-kicker" x="1058" y={centerY-27}>IRREVERSIBLE OUTFLOW</text>
          <text className="burn-sankey__out-label" x="1058" y={centerY-2}>FLR burned</text>
          <text className="burn-sankey__out-value" x="1058" y={centerY+22}>{formatFlr(totalBurnWei)} FLR</text>
        </svg>
      </div>
      <span className="burn-sankey__mobile-hint" aria-hidden="true">Swipe to follow the flow →</span>
      <div className="burn-sankey__footer">
        <span className="burn-sankey__signal"><i/><span ref={signalText} aria-live="polite">Waiting for a confirmed block</span></span>
        <b>Particles mirror each Burnstream bar · labels show 30-day share</b>
      </div>
      <details className="burn-sankey__details">
        <summary>View all {allSources.length} measured source values</summary>
        <div className="burn-sankey__detail-list">
          {allSources.map((source)=><div key={source.family}><span><strong>{source.label}</strong><small>{source.confidence} attribution</small></span><b>{formatFlr(source.amountWei)} FLR</b><em>{formatShare(source.shareBps)}</em></div>)}
        </div>
      </details>
    </section>
  );
});
